import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  faChevronDown,
  faChevronUp,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { useGetCategories } from "~/api/dropshipping/queries";
import { IOnboardingChoices } from "~/types/account";
import * as S from "./styles";
import Loader from "../ui/Loader";
import { SVG } from "../ui/SVG";

type Props = {
  onboardingChoices: IOnboardingChoices | undefined;
  setOnboardingChoices: Dispatch<
    SetStateAction<IOnboardingChoices | undefined>
  >;
};

export const SelectCategory = ({
  onboardingChoices,
  setOnboardingChoices,
}: Props) => {
  const { data: categories } = useGetCategories();
  const [isOtherOpen, setIsOtherOpen] = useState(false);
  const otherWrapperRef = useRef<HTMLDivElement>(null);
  const otherButtonRef = useRef<HTMLButtonElement>(null);
  const otherPortalRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 320,
  });

  const normalizeCategoryName = (name: string) =>
    name.replace(/\s+/g, " ").trim().toLowerCase();

  const orderedCategories = [
    { name: "Home Appliances", aliases: ["Home Appliances"] },
    { name: "Bath & Beauty", aliases: ["Bath & Beauty", "Beauty & Health"] },
    { name: "Home & Garden", aliases: ["Home & Garden"] },
    { name: "Accessories", aliases: ["Accessories"] },
    {
      name: "Jewelry & Accessories",
      aliases: ["Jewelry & Accessories", "Jewelry &\nAccessories"],
    },
    { name: "Tech Accessories", aliases: ["Tech Accessories"] },
    { name: "Women's Clothing", aliases: ["Women's Clothing"] },
    { name: "Men's Clothing", aliases: ["Men's Clothing"] },
    { name: "Pet Products", aliases: ["Pet Products", "Pets"] },
  ];

  const sourceCategories = categories && categories.length > 0 ? categories : [];

  const filteredCategories =
    sourceCategories.filter((category) =>
      orderedCategories.some((orderedCategory) =>
        orderedCategory.aliases.some(
          (alias) =>
            normalizeCategoryName(alias) ===
            normalizeCategoryName(category.name)
        )
      )
    ) || [];

  const sortedCategories = useMemo(
    () =>
      orderedCategories
        .map((orderedCategory) => {
          const matchedCategory = filteredCategories.find((category) =>
            orderedCategory.aliases.some(
              (alias) =>
                normalizeCategoryName(alias) ===
                normalizeCategoryName(category.name)
            )
          );

          if (!matchedCategory) return undefined;

          return {
            ...matchedCategory,
            name: orderedCategory.name,
          };
        })
        .filter(Boolean),
    [filteredCategories]
  );

  const primaryCategoryNames = useMemo(
    () => [
      "Women's Clothing",
      "Men's Clothing",
      "Tech Accessories",
      "Bath & Beauty",
    ],
    []
  );

  const primaryCategories = useMemo(
    () =>
      sortedCategories.filter(
        (category) => category && primaryCategoryNames.includes(category.name)
      ),
    [primaryCategoryNames, sortedCategories]
  );

  const otherCategories = useMemo(
    () =>
      sortedCategories.filter(
        (category) => category && !primaryCategoryNames.includes(category.name)
      ),
    [primaryCategoryNames, sortedCategories]
  );

  const selectedCategoryIds = onboardingChoices?.selectedCategories || [];

  const selectedOtherCount = useMemo(() => {
    const otherCategoryIdSet = new Set(
      otherCategories
        .map((category) => category?.id)
        .filter((id): id is string | number => id !== null && id !== undefined)
    );

    return selectedCategoryIds.filter(
      (id): id is string | number =>
        id !== null && id !== undefined && otherCategoryIdSet.has(id)
    ).length;
  }, [otherCategories, selectedCategoryIds]);

  const otherLabel =
    selectedOtherCount > 0 && !isOtherOpen
      ? `(${selectedOtherCount}) Categories selected`
      : "Other";

  useEffect(() => {
    if (onboardingChoices?.selectedCategories) return;

    setOnboardingChoices((prev) => ({
      ...prev,
      selectedCategories: [],
    }));
  }, [onboardingChoices?.selectedCategories, setOnboardingChoices]);

  useEffect(() => {
    if (!isOtherOpen) return;

    const updateMenuPosition = () => {
      if (!otherButtonRef.current) return;

      const rect = otherButtonRef.current.getBoundingClientRect();
      const margin = 8;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const shouldOpenUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      const calculatedMaxHeight = Math.max(
        160,
        Math.min(320, shouldOpenUp ? spaceAbove : spaceBelow)
      );

      const top = shouldOpenUp
        ? Math.max(margin, rect.top - calculatedMaxHeight - 4)
        : rect.bottom + 4;

      setMenuPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: calculatedMaxHeight,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOtherOpen]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const isInsideTrigger =
        otherWrapperRef.current && otherWrapperRef.current.contains(targetNode);
      const isInsidePortal =
        otherPortalRef.current && otherPortalRef.current.contains(targetNode);

      if (
        isOtherOpen &&
        !isInsideTrigger &&
        !isInsidePortal
      ) {
        setIsOtherOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOtherOpen]);

  const handleCategorySelection = (id: string | number) => {
    setOnboardingChoices((prev) => {
      const prevSelected = prev?.selectedCategories || [];

      if (prevSelected.includes(id)) {
        return {
          ...prev,
          selectedCategories: prevSelected.filter((categoryId) => categoryId !== id),
        };
      }

      return {
        ...prev,
        selectedCategories: [...prevSelected.filter((categoryId) => categoryId !== null), id],
      };
    });
  };

  const clearOtherSelection = () => {
    const otherCategoryIdSet = new Set(
      otherCategories
        .map((category) => category?.id)
        .filter((id): id is string | number => id !== null && id !== undefined)
    );

    setOnboardingChoices((prev) => {
      const prevSelected = prev?.selectedCategories || [];
      return {
        ...prev,
        selectedCategories: prevSelected.filter(
          (categoryId) =>
            categoryId === null ||
            categoryId === undefined ||
            !otherCategoryIdSet.has(categoryId)
        ),
      };
    });
  };

  return (
    <S.CategoryModalContent>
      <S.CardContainer>
        {sortedCategories.length > 0 ? (
          <>
            <S.PrimaryCategoriesContainer>
              {primaryCategories.map((category) => {
                if (!category) return null;

                return (
                  <S.PrimaryCategoryCard
                    key={category.id}
                    className={
                      selectedCategoryIds.includes(category.id) ? "active" : ""
                    }
                    onClick={() => handleCategorySelection(category.id)}>
                    {category.name}
                  </S.PrimaryCategoryCard>
                );
              })}
            </S.PrimaryCategoriesContainer>

            <S.OtherDropdownWrapper ref={otherWrapperRef}>
              <S.OtherDropdownButton
                ref={otherButtonRef}
                className={isOtherOpen ? "open" : ""}
                onClick={() => setIsOtherOpen((prev) => !prev)}>
                <S.OtherDropdownLabel className={selectedOtherCount > 0 ? "hasSelection" : ""}>
                  {selectedOtherCount > 0 && !isOtherOpen && (
                    <S.ClearOtherSelectionButton
                      onClick={(event) => {
                        event.stopPropagation();
                        clearOtherSelection();
                      }}>
                      <SVG icon={faXmark} />
                    </S.ClearOtherSelectionButton>
                  )}
                  {otherLabel}
                </S.OtherDropdownLabel>
                <SVG icon={isOtherOpen ? faChevronUp : faChevronDown} />
              </S.OtherDropdownButton>

              {isOtherOpen &&
                createPortal(
                  <S.OtherDropdownPortalMenu
                    ref={otherPortalRef}
                    style={{
                      top: `${menuPosition.top}px`,
                      left: `${menuPosition.left}px`,
                      width: `${menuPosition.width}px`,
                      maxHeight: `${menuPosition.maxHeight}px`,
                    }}>
                    {otherCategories.map((category) => {
                      if (!category) return null;

                      return (
                        <S.OtherDropdownOption
                          key={category.id}
                          className={
                            selectedCategoryIds.includes(category.id) ? "active" : ""
                          }
                          onClick={() => handleCategorySelection(category.id)}>
                          {category.name}
                        </S.OtherDropdownOption>
                      );
                    })}
                  </S.OtherDropdownPortalMenu>,
                  document.body
                )}
            </S.OtherDropdownWrapper>
          </>
        ) : (
          <S.LoaderContainer>
            <Loader fullWidth />
          </S.LoaderContainer>
        )}
      </S.CardContainer>
    </S.CategoryModalContent>
  );
};
